const FORCE_LOGIN_KEY = 'forceLogin'
const SKIP_AUTH_CACHE_KEY = 'skipAuthCache'

/**
 * Base cookie names. `stageCookieName()` appends the stage — see the note there for why the
 * *name* carries the stage rather than the `domain` attribute.
 */
const FORCE_LOGIN_COOKIE = 'ps-force-login'
const SKIP_AUTH_CACHE_COOKIE = 'ps-skip-auth-cache'

export const AUTH_REDIRECT_URL_KEY = 'authRedirectUrl'

// Exported for unit testing with synthetic hostnames, without stubbing `window.location`.
export function getCookieDomains(hostname: string) {
  const isIpv4Hostname = hostname
    .split('.')
    .every((part) => part.length > 0 && Number.isInteger(Number(part)) && Number(part) >= 0 && Number(part) <= 255)

  if (!hostname || hostname === 'localhost' || isIpv4Hostname) {
    return [undefined]
  }

  const parts = hostname.split('.')
  if (parts.length < 2) {
    return [undefined]
  }

  // The apex, always. #22381 tried to isolate stages here by returning `.${stage}.${apex}` —
  // `.test.paintscout.com` for host `test.app.paintscout.com` — but the stage label sits in front
  // of the *app* label, not the apex, so that string is not a domain-suffix of the host. Browsers
  // reject such a `Set-Cookie` outright (RFC 6265 §5.3), which silently left only the host-only
  // cookie and broke the app -> painter-view handoff on test and staging.
  //
  // `test.app.paintscout.com` and `test.painters.paintscout.com` share no suffix other than
  // `paintscout.com`, so cross-host scoping within a stage *requires* the apex. Stage isolation is
  // done by cookie name instead — see `stageCookieName`.
  return [undefined, `.${parts.slice(-2).join('.')}`]
}

// Warn once per page load rather than once per call site (every write, read and clear consults
// this) — a build that never got `DEPLOYMENT_STAGE` injected silently falls back to the
// un-suffixed production name, which is the cross-stage bleed this is meant to prevent.
let hasWarnedMissingStage = false

/**
 * Stage-qualified cookie name.
 *
 * Every stage shares the `paintscout.com` apex (`test.app.paintscout.com` vs
 * `app.paintscout.com`), and a cookie set on the apex is visible to all of them. So a logout on
 * test set `ps-force-login` for production and every other stage, and whichever app loaded first
 * consumed the flag out from under the app that set it (ENG-1237). Since the domain cannot carry
 * the stage, the name does.
 *
 * `master` keeps the bare name deliberately: production's cookie name is unchanged, so no
 * production user is affected by this rollout, and production can never see a non-prod flag.
 */
function stageCookieName(name: string) {
  const stage = process.env.DEPLOYMENT_STAGE

  if (!stage) {
    if (!hasWarnedMissingStage) {
      hasWarnedMissingStage = true
      // eslint-disable-next-line no-console
      console.warn(
        '[react-auth0] DEPLOYMENT_STAGE is unset — force-login/skip-auth-cache cookies will use the un-scoped production name'
      )
    }
    return name
  }

  return stage === 'master' ? name : `${name}-${stage}`
}

function writeCookie(name: string, value: string, expires: string) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  getCookieDomains(window.location.hostname).forEach((domain) => {
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}${secure}`
  })
}

function hasCookie(name: string, value: string) {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .some((cookie) => cookie === `${name}=${value}`)
}

function clearCookie(name: string) {
  writeCookie(name, '', 'Thu, 01 Jan 1970 00:00:00 GMT')
}

// `localStorage` can throw or evaluate to `null` (Safari ITP, private browsing, storage
// partitioning around the cross-origin Auth0 redirect) — every accessor in this file is
// wrapped through here so a broken localStorage can never crash or hang the login flow.
// The cookie-backed keys below still work even when this fails, since writeCookie/hasCookie
// don't touch localStorage.
//
// Warn once per page load rather than once per call site — a single broken-localStorage
// load touches this from several functions in the same render/effect pass, and repeating
// the warning that many times just buries other console signal.
let hasWarnedStorageUnavailable = false

function safeStorage<T>(fn: () => T, fallback: T): T
function safeStorage<T>(fn: () => T): T | undefined
function safeStorage<T>(fn: () => T, fallback?: T): T | undefined {
  try {
    return fn()
  } catch (err) {
    if (!hasWarnedStorageUnavailable) {
      hasWarnedStorageUnavailable = true
      // eslint-disable-next-line no-console
      console.warn('[react-auth0] localStorage unavailable', err)
    }
    return fallback
  }
}

/**
 * Transitional: also honour the pre-rename cookie for one release.
 *
 * During a deploy, a browser holding the old bundle logs out and writes the un-suffixed name,
 * then loads the new bundle which looks for the suffixed one. Without this read the flag is
 * missed, `prompt: 'login'` is not sent, and Auth0's live SSO session silently re-issues the
 * account the user just left — the "I have to log in twice" report itself.
 *
 * On a non-prod stage this does re-admit production's un-suffixed cookie for that one release.
 * That is the pre-existing ENG-1237 behaviour, not a new regression, and it costs at most an
 * extra login prompt on test.
 *
 * TODO(ENG-1237): remove `readsLegacyCookie`, `hasCookieIncludingLegacy`, `clearCookieIncludingLegacy`
 * and the "still honours the pre-rename cookie name" test in `login.cookieScope.spec.ts` once
 * 7.13.x is fully rolled out. Tracked on ENG-1237 / #22378, which this back-port closes.
 */
function readsLegacyCookie(name: string) {
  return stageCookieName(name) !== name
}

function hasCookieIncludingLegacy(name: string, value: string) {
  return hasCookie(stageCookieName(name), value) || (readsLegacyCookie(name) && hasCookie(name, value))
}

function clearCookieIncludingLegacy(name: string) {
  clearCookie(stageCookieName(name))
  if (readsLegacyCookie(name)) {
    clearCookie(name)
  }
}

export function markForceLogin() {
  safeStorage(() => localStorage.setItem(FORCE_LOGIN_KEY, 'true'))
  safeStorage(() => localStorage.setItem(SKIP_AUTH_CACHE_KEY, 'true'))
  const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
  writeCookie(stageCookieName(FORCE_LOGIN_COOKIE), '1', expires)
  writeCookie(stageCookieName(SKIP_AUTH_CACHE_COOKIE), '1', expires)
}

// Sets the cache-bypass signal only — without the login-form prompt.
// Used when redirecting a painter from the main app to painter-view so that
// painter-view discards any stale non-painter session it has cached locally.
export function markSkipAuthCache() {
  safeStorage(() => localStorage.setItem(SKIP_AUTH_CACHE_KEY, 'true'))
  const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
  writeCookie(stageCookieName(SKIP_AUTH_CACHE_COOKIE), '1', expires)
}

export function consumeForceLogin() {
  // Only honor the cookie — not the localStorage key alone.
  // The localStorage key can be stale (set by a previous logout on this origin and
  // never consumed because the cross-app SSO flow bypassed the login form). The
  // cookie has a 5-minute expiry and is always fresh when present. This means a
  // cross-app redirect from a freshly-authenticated painter will never accidentally
  // show the login form a second time due to a lingering localStorage key.
  const shouldForceLogin = hasCookieIncludingLegacy(FORCE_LOGIN_COOKIE, '1')

  safeStorage(() => localStorage.removeItem(FORCE_LOGIN_KEY)) // always clean up, even if stale
  clearCookieIncludingLegacy(FORCE_LOGIN_COOKIE)

  return shouldForceLogin
}

export function shouldSkipAuthCache() {
  // The `=== 'true'` comparison is done *inside* the safeStorage callback (not on its
  // result) so that a thrown read falls back to the fallback below directly, rather than
  // comparing `undefined === 'true'` outside the try boundary. Fail safe, not fail open:
  // when localStorage can't be read at all, we can't rule out a session that should have
  // been invalidated, so default to `true` (skip/discard the cache) rather than `false`
  // (trust it). The cookie check still runs and can also independently say `true`.
  return (
    safeStorage(() => localStorage.getItem(SKIP_AUTH_CACHE_KEY) === 'true', true) ||
    hasCookieIncludingLegacy(SKIP_AUTH_CACHE_COOKIE, '1')
  )
}

export function clearSkipAuthCache() {
  safeStorage(() => localStorage.removeItem(SKIP_AUTH_CACHE_KEY))
  clearCookieIncludingLegacy(SKIP_AUTH_CACHE_COOKIE)
}

const BRANCH_PREFIX_RE = /^\/_branch\/[^/]+/

/**
 * Routes that only exist to move the user through a login or logout. None of them is a place to
 * return to after a login: `/logout` logs the user straight back out, `/login` fires another
 * `loginWithRedirect`, and `/login-callback` re-enters `handleRedirectCallback` with a consumed
 * authorization code.
 *
 * Matched with the `/_branch/{name}` prefix stripped, since `window.location.pathname` carries it
 * on a branch deploy (see `storeAuthRedirectUrl`).
 */
const TRANSIENT_AUTH_PATHS = ['/logout', '/login', '/login-callback']

/**
 * True when `url` points at one of the transient auth routes above.
 *
 * Exported for the guards in `useAuthCallback` and `authRecovery`, and for its own tests.
 * `/login-callback` is deliberately not treated as a child of `/login`: the check is an exact
 * path match or a `/`-delimited child, so `/login/:slug` counts and `/login-callback` is matched
 * by its own entry rather than by the `/login` one.
 */
export function isTransientAuthPath(url?: string | null): boolean {
  if (!url) {
    return false
  }

  const path = url.split(/[?#]/)[0].replace(BRANCH_PREFIX_RE, '').replace(/\/+$/, '') || '/'

  return TRANSIENT_AUTH_PATHS.some((authPath) => path === authPath || path.startsWith(`${authPath}/`))
}

/**
 * Remember where to return to after the Auth0 round trip. Defaults to the current
 * location including its query string — the query is load-bearing (e.g. the admin
 * masquerade link's `?masqueradeCompanyId=`), and dropping it silently lands a
 * superadmin in their own account instead of the client's.
 *
 * On a branch deploy `window.location.pathname` still carries the `/_branch/{name}`
 * prefix (it is not basename-stripped), so stashing it is also what gets the user
 * back onto the branch after Auth0 returns to the canonical origin.
 *
 * Only call this when a login redirect is actually about to happen — a stashed value
 * lives in localStorage until it's consumed by the callback, so writing it on a path
 * that never reaches /login-callback leaves a stale value that hijacks a later login.
 *
 * A transient auth route is never stored, and any value already there is cleared instead
 * (ENG-1190). `recoverFromMissingToken` calls this with whatever page the user is on, and on a
 * fast account switch that page is `/logout` — a token request fired by the page load settles
 * after the user has already clicked Log Out. `authRedirectUrl` is on `clearLocalStorage`'s
 * permanent whitelist, so the logout does not clear it; the next login callback then redirected
 * the user straight back to `/logout`, logged them out again, and made them log in twice.
 */
export function storeAuthRedirectUrl(url?: string) {
  const target = url ?? `${window.location.pathname}${window.location.search}`

  if (isTransientAuthPath(target)) {
    clearAuthRedirectUrl()
    return
  }

  safeStorage(() => localStorage.setItem(AUTH_REDIRECT_URL_KEY, target))
}

export function clearAuthRedirectUrl() {
  safeStorage(() => localStorage.removeItem(AUTH_REDIRECT_URL_KEY))
}

// Safe read counterpart to storeAuthRedirectUrl/clearAuthRedirectUrl — used by
// useAuthCallback on the /login-callback page so a broken localStorage can't throw
// before handleRedirectCallback() is even called.
export function getAuthRedirectUrl(): string | null {
  return safeStorage(() => localStorage.getItem(AUTH_REDIRECT_URL_KEY), null)
}

// Reads the auth0-spa-js cached user entry directly out of localStorage — used by
// AuthProvider to seed session state on initial render, before the async check in its
// initializeAuth effect resolves. Every localStorage touch (`length`/`key`/`getItem`) and
// the JSON parse of a corrupt/partial entry are covered by the same safeStorage boundary,
// so a broken store falls back to "no cached user" instead of throwing during render.
export function getCachedAuth0User(): Record<string, any> | undefined {
  return safeStorage(() => {
    const regex = /^@@auth0spajs@@::.+::@@user@@$/

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && regex.test(key)) {
        const foundItem = localStorage.getItem(key)
        if (foundItem) {
          return JSON.parse(foundItem)
        }
      }
    }
  })
}

export function getLastLoginHint(): string | undefined {
  const lastLogin = safeStorage(() => localStorage.getItem('lastLogin'), null)

  if (!lastLogin) {
    return undefined
  }

  try {
    const parsed = JSON.parse(lastLogin)
    return typeof parsed === 'string' ? parsed : undefined
  } catch {
    return lastLogin
  }
}

export function getLoginAuthorizationParams(extraParams: Record<string, string | undefined> = {}) {
  const forceLogin = consumeForceLogin()

  return {
    login_hint: getLastLoginHint(),
    ...extraParams,
    ...(forceLogin ? { prompt: 'login' as const } : {})
  }
}
